import React, { useEffect, useState } from 'react';
import { API, axios } from '../../utils/api';
import Icona from '../cliente/Icone';

// Impostazioni → Stato push (solo admin, GET /api/push/stato): risponde alla domanda «perché le notifiche non
// arrivano?» senza aprire i log del server. Quale chiave VAPID sta usando il server adesso, se la configurazione
// è completa, quanti telefoni sono iscritti e su quale servizio, quanti hanno un'iscrizione che non vale più
// (fatta con una chiave di prima: vanno riattivati dal telefono, Profilo → Notifiche) e gli errori delle ultime
// 24 ore raggruppati per motivo.
//
// Nasce dal caso vero dell'iPhone muto: Apple rispondeva 403 VapidPkHashMismatch a ogni invio e a video non si
// vedeva niente. La chiave arriva già troncata dal server: quella intera a video non serve a nessuno.

// I motivi del server (backend/push_service.py: ESITO_*) a parole
const MOTIVI = {
  chiave_diversa: { testo: 'iscrizione fatta con una chiave diversa: va riattivata dal telefono', tag: 't-r' },
  scaduta: { testo: 'il telefono non ha più l\'app o ha spento le notifiche', tag: 't-w' },
  errore: { testo: 'rifiutata dal servizio push', tag: 't-r' },
  consegnata: { testo: 'consegnata al servizio', tag: 't-g' },
};

export const motivo = (esito) => MOTIVI[esito] || { testo: esito || 'motivo sconosciuto', tag: 't-w' };

// "Apple 4 · Google 3", dal più numeroso
export const perServizio = (conti) => Object.entries(conti || {})
  .sort((a, b) => b[1] - a[1]).map(([nome, quanti]) => `${nome} ${quanti}`).join(' · ');

const StatoPush = () => {
  const [dati, setDati] = useState(null);
  const [problema, setProblema] = useState(false);
  useEffect(() => { axios.get(`${API}/push/stato`).then((r) => setDati(r.data)).catch(() => setProblema(true)); }, []);

  if (problema) {
    return (
      <div className="imp-griglia" data-testid="stato-push-problema">
        <section className="card">
          <div className="ttl"><div className="i r"><Icona nome="avviso" /></div><h2>Stato push</h2></div>
          <p className="imp-nota">Lo stato delle notifiche non è arrivato dal server: ricaricate la pagina fra poco.</p>
        </section>
      </div>
    );
  }
  if (!dati) return <p className="vuoto">Carico lo stato delle notifiche…</p>;

  const errori = dati.errori_24h || [];
  const daRiattivare = dati.da_riattivare || 0;
  return (
    <div className="imp-griglia" data-testid="stato-push">
      <section className="card">
        <div className="ttl"><div className="i"><Icona nome="campanella" /></div><h2>Stato push</h2>
          <span className={`tag ${dati.chiave_configurata ? 't-g' : 't-r'}`} data-testid="push-configurazione">
            {dati.chiave_configurata ? 'configurazione completa' : 'configurazione incompleta'}
          </span>
        </div>
        <p className="imp-nota">
          Le notifiche si firmano con una coppia di chiavi VAPID. Se sul server la chiave cambia, le iscrizioni fatte con quella di prima
          smettono di funzionare: il servizio push risponde 403 e il telefono resta muto finché non si riattivano da Profilo → Notifiche.
        </p>
        <div className="kv" style={{ marginTop: '.8rem' }}>
          <div><small>Chiave pubblica in uso</small><b className={dati.chiave_pubblica ? undefined : 'vuoto'} data-testid="push-chiave">{dati.chiave_pubblica || 'non impostata'}</b></div>
          <div><small>Mittente (VAPID_CLAIMS_EMAIL)</small><b className={dati.mittente ? undefined : 'vuoto'}>{dati.mittente || 'non impostato'}</b></div>
          <div><small>Telefoni iscritti</small><b data-testid="push-iscrizioni">{dati.iscrizioni || 0}{perServizio(dati.per_servizio) ? ` · ${perServizio(dati.per_servizio)}` : ''}</b></div>
          <div><small>Da riattivare dal telefono</small><b className={daRiattivare ? 'no' : 'g'} data-testid="push-da-riattivare">{daRiattivare}</b></div>
          <div><small>Iscrizioni con una chiave vecchia</small><b className={dati.con_chiave_vecchia ? 'w' : 'g'}>{dati.con_chiave_vecchia || 0}</b></div>
          <div><small>Errori nelle ultime 24 ore</small><b className={dati.errori_totali_24h ? 'no' : 'g'}>{dati.errori_totali_24h || 0}</b></div>
        </div>
        {daRiattivare > 0 && (
          <p className="imp-nota" data-testid="push-avviso-riattivare" style={{ marginTop: '.8rem' }}>
            <b>{daRiattivare === 1 ? 'Un telefono ha' : `${daRiattivare} telefoni hanno`} un’iscrizione che non vale più.</b>{' '}
            Va rifatta da quel telefono: Profilo → Notifiche → «Riattiva le notifiche». Da qui non si può fare.
          </p>
        )}
      </section>

      <section className="card">
        <div className="ttl"><div className="i b"><Icona nome="avviso" /></div><h2>Errori delle ultime 24 ore <small>{dati.errori_totali_24h || 0}</small></h2></div>
        {errori.length === 0 ? (
          <p className="imp-nota" data-testid="push-niente-errori">Tutto a posto: nessuna notifica rifiutata nelle ultime 24 ore.</p>
        ) : (
          <div className="tabella-box">
            <table className="tabella">
              <thead><tr><th>Motivo</th><th>Codice</th><th>Quante</th><th>Esempio</th></tr></thead>
              <tbody>
                {errori.map((e) => {
                  const m = motivo(e.esito);
                  return (
                    <tr key={`${e.esito}-${e.codice}`} data-testid={`push-errore-${e.esito}`}>
                      <td><span className={`tag ${m.tag}`}>{e.esito}</span><small>{m.testo}</small></td>
                      <td className="nowrap">{e.codice || '—'}</td>
                      <td className="nowrap">{e.quanti}</td>
                      <td>{e.esempio || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default StatoPush;
